import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Flame, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { fetchCurrentAuction } from "@/lib/api";

const DISMISS_KEY = "hac_auction_ribbon_dismissed";

const compactRemaining = (secs) => {
  if (!secs || secs <= 0) return "";
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  if (d > 0) return `${d}d ${h}h`;
  const m = Math.floor((secs % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

/**
 * Compact site-wide auction ribbon. Renders ABOVE the header on every page
 * EXCEPT the auction page itself. Dismissable per session.
 */
const AuctionHeaderRibbon = () => {
  const { lang } = useLang();
  const isNl = lang === "nl";
  const location = useLocation();
  const [a, setA] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === "1"; } catch (_) { return false; }
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchCurrentAuction();
        if (!cancelled && data && data.id && data.status === "open") setA(data);
      } catch (e) { /* no auction → render nothing */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!a || dismissed) return null;
  // Show ONLY on the advertise pages — keep the homepage and content pages clean.
  const ADVERTISE_PATHS = ["/sponsor", "/premium-listing", "/premium"];
  if (!ADVERTISE_PATHS.includes(location.pathname)) return null;

  const bidCount = a.bid_count || 0;
  const highest = a.current_highest_bid_eur;
  const minNext = a.min_next_bid_eur || a.starting_price_eur || 49;
  const remaining = compactRemaining(a.seconds_remaining);

  const onDismiss = (e) => {
    e.preventDefault(); e.stopPropagation();
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch (_) { /* noop */ }
    setDismissed(true);
  };

  return (
    <Link
      to="/auction"
      className="block bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 text-white text-xs hover:from-amber-600 hover:via-orange-600 hover:to-amber-600 transition-colors relative"
      data-testid="auction-header-ribbon"
    >
      <div className="hac-container py-2 flex items-center justify-center gap-2 sm:gap-4 flex-wrap text-center">
        <Flame size={14} className="shrink-0 animate-pulse" />
        <span className="font-bold uppercase tracking-widest text-[10px] hidden sm:inline">{isNl ? "Live veiling" : "Live auction"}</span>
        <span className="opacity-30 hidden sm:inline">·</span>
        <span>
          {bidCount > 0
            ? (highest
                ? (isNl ? <>Top bod <strong>€{Math.round(highest)}</strong> · {bidCount} {bidCount === 1 ? "bedrijf" : "bedrijven"} biedt mee</> : <>Top bid <strong>€{Math.round(highest)}</strong> · {bidCount} {bidCount === 1 ? "company" : "companies"} bidding</>)
                : (isNl ? <>Bieden vanaf <strong>€{Math.round(minNext)}</strong></> : <>Bid from <strong>€{Math.round(minNext)}</strong></>))
            : (isNl ? <>Open spot deze week · vanaf <strong>€{Math.round(minNext)}</strong></> : <>Open slot this week · from <strong>€{Math.round(minNext)}</strong></>)}
        </span>
        {remaining && (<><span className="opacity-30">·</span><span className="font-mono">{remaining} {isNl ? "te gaan" : "left"}</span></>)}
        <span className="underline font-bold ml-1">{isNl ? "Bekijk →" : "View →"}</span>
      </div>
      <button onClick={onDismiss} aria-label="Dismiss" className="absolute right-2 top-1/2 -translate-y-1/2 hover:bg-black/10 rounded-full p-1 transition-colors" data-testid="auction-header-ribbon-dismiss">
        <X size={14} />
      </button>
    </Link>
  );
};

export default AuctionHeaderRibbon;
